/**
 * Preview pane for the plugin builder.
 * Renders the built plugin in a sandboxed iframe with device sizing,
 * undo/redo controls, graph, code, and log tabs.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverContent, PopoverTrigger, PopoverClose } from '@/components/ui/popover';
import {
  Loader2,
  RefreshCw,
  Code2,
  Eye,
  AlertCircle,
  Undo2,
  Redo2,
  Monitor,
  Tablet,
  Smartphone,
  Sun,
  Moon,
  GitBranch,
  ScrollText,
  Eraser,
  Settings,
  X,
  Maximize2,
  Minimize2,
} from 'lucide-react';
import { PLUGIN_IFRAME_ALLOW, PLUGIN_IFRAME_SANDBOX } from '../../../components/pluginSandbox';
import { CodeViewerPane } from './CodeViewerPane';
import { GraphPane } from './GraphPane';
import { LogContent } from './LogContent';
import type { VirtualFileSystem, ProcessGraph, LogEntry, AiConfig } from '../types';
import type { ModelEntity } from '@/lib/api';

type DeviceSize = 'desktop' | 'tablet' | 'mobile';
type Tab = 'preview' | 'graph' | 'code' | 'log';

const DEVICE_WIDTHS: Record<DeviceSize, string> = {
  desktop: '100%',
  tablet: '768px',
  mobile: '375px',
};

interface PluginPreviewPaneProps {
  previewHtml: string | null;
  files: VirtualFileSystem | null;
  isBuildingPreview: boolean;
  buildError: string | null;
  onBuildPreview: () => Promise<string | null>;
  /** A fresher build is stashed (deferred swap, Phase 5) — show the pill. */
  hasPendingPreview?: boolean;
  /** Apply the stashed preview. Cooperative state capture runs first — only
   *  state the plugin persists (usePersistentState / captureState) survives. */
  onApplyPendingPreview?: () => void;
  // Undo/redo
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  undoCount?: number;
  redoCount?: number;
  // Admin controls
  verbose?: boolean;
  onVerboseChange?: (value: boolean) => void;
  deepThinking?: boolean;
  onDeepThinkingChange?: (value: boolean) => void;
  isAdmin?: boolean;
  // Graph
  processGraph?: ProcessGraph;
  currentNodeId?: string | null;
  aiConfigs?: AiConfig[];
  /** Model definitions for resolving display names in graph */
  modelDefinitions?: ModelEntity[];
  // Logs
  logEntries?: LogEntry[];
  onClearLogs?: () => void;
  // Screenshots
  onScreenshotCaptured?: (dataUrl: string) => void;
  // Preview theme
  previewTheme?: 'light' | 'dark';
  onPreviewThemeToggle?: () => void;
  // Navigate to node callback (exposed for parent wiring)
  onNavigateToNode?: (nodeId: string) => void;
  // Ref to the preview iframe — owned by the parent so it can postMessage to the iframe
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  /** Bridge ref for sending bridge-protocol messages (theme, step navigation). */
  bridgeRef?: React.RefObject<import('../../../components/PluginBridge').PluginBridgeHost | null>;
  /** Called when the preview iframe finishes loading (for bridge initialization). */
  onPreviewIframeLoad?: () => void;
  /**
   * Wipes the plugin's server-persisted preview data (storage, data-store
   * collections, workspaces) and rebuilds the preview. Preview data survives
   * rebuilds by design (production parity), so this is the recovery path for
   * stale-schema data mid-build. Rejects (and must NOT rebuild) on failure.
   */
  onResetPreviewData?: () => Promise<void>;
  className?: string;
}

export function PluginPreviewPane({
  previewHtml,
  files,
  isBuildingPreview,
  buildError,
  onBuildPreview,
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  undoCount = 0,
  redoCount = 0,
  verbose = false,
  onVerboseChange,
  deepThinking = false,
  onDeepThinkingChange,
  isAdmin = false,
  processGraph,
  currentNodeId,
  aiConfigs,
  modelDefinitions,
  logEntries = [],
  onClearLogs,
  onScreenshotCaptured,
  previewTheme,
  onPreviewThemeToggle,
  iframeRef,
  bridgeRef,
  onPreviewIframeLoad,
  hasPendingPreview,
  onApplyPendingPreview,
  onResetPreviewData,
  onNavigateToNode,
  className,
}: PluginPreviewPaneProps) {
  const [activeTab, setActiveTab] = useState<Tab>('preview');
  const [deviceSize, setDeviceSize] = useState<DeviceSize>('desktop');
  const [isResettingData, setIsResettingData] = useState(false);
  // Full-screen expands THIS pane's root to cover the viewport via CSS only
  // (`fixed inset-0`). The pane must stay in the same tree position — a
  // portal or conditional re-parent would remount the preview iframe and
  // wipe the running plugin's state.
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Escape exits full screen — best-effort: keystrokes land here only while
  // focus is outside the plugin iframe, so the minimize button stays the
  // canonical exit.
  useEffect(() => {
    if (!isFullscreen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isFullscreen]);
  const onScreenshotCapturedRef = useRef(onScreenshotCaptured);
  onScreenshotCapturedRef.current = onScreenshotCaptured;
  const fileCount = files ? Object.keys(files).length : 0;
  const hasGraph = processGraph && processGraph.nodes.length > 0;

  // Navigate to a specific node's view in the preview iframe
  const navigateToNode = useCallback(
    (nodeId: string) => {
      setActiveTab('preview');
      bridgeRef?.current?.navigateToStep(nodeId);
      onNavigateToNode?.(nodeId);
    },
    [bridgeRef, onNavigateToNode],
  );

  // Listen for postMessage events from the iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from our own preview iframe
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (!event.data || typeof event.data !== 'object') return;

      if (event.data.type === 'SCREENSHOT_RESULT' && event.data.dataUrl) {
        onScreenshotCapturedRef.current?.(event.data.dataUrl);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // Send theme to iframe via bridge protocol (avoids srcDoc replacement which reloads the iframe)
  useEffect(() => {
    if (!previewHtml) return;
    bridgeRef?.current?.sendThemeUpdate();
  }, [previewTheme, previewHtml, bridgeRef]);

  // Trigger screenshot capture and bridge init after preview loads
  const handleIframeLoad = useCallback(() => {
    if (!iframeRef.current?.contentWindow) return;
    // Initialize bridge first, then capture screenshot after render settles
    onPreviewIframeLoad?.();
    if (onScreenshotCapturedRef.current) {
      setTimeout(() => {
        iframeRef.current?.contentWindow?.postMessage({ type: 'CAPTURE_SCREENSHOT' }, '*');
      }, 500);
    }
  }, [onPreviewIframeLoad]);

  // Available tabs
  const tabs: Array<{
    key: Tab;
    label: string;
    icon: React.ElementType;
    badge?: number;
  }> = [
    { key: 'preview', label: 'Preview', icon: Eye },
    ...(hasGraph ? [{ key: 'graph' as Tab, label: 'Graph', icon: GitBranch }] : []),
    {
      key: 'code',
      label: 'Code',
      icon: Code2,
      badge: fileCount > 0 ? fileCount : undefined,
    },
    {
      key: 'log',
      label: 'Log',
      icon: ScrollText,
      badge: logEntries.length > 0 ? logEntries.length : undefined,
    },
  ];

  return (
    <div
      className={cn(
        'flex flex-col h-full',
        className,
        // Same element, same tree position — only the positioning changes, so
        // the always-mounted preview iframe keeps its state across toggles.
        isFullscreen && 'fixed inset-0 z-50 bg-background',
      )}
    >
      {/* Tab bar + controls */}
      <div className="flex items-center border-b bg-muted/20 px-3 py-1.5 gap-1 flex-shrink-0 flex-wrap min-h-[41px]">
        {/* Tabs */}
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              'flex items-center gap-1.5 text-xs font-medium px-3 py-1 rounded-md transition-colors',
              activeTab === tab.key
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <tab.icon className="h-3.5 w-3.5" />
            {tab.label}
            {tab.badge != null && (
              <span className="text-[10px] px-1 rounded-full bg-muted text-muted-foreground">
                {tab.badge}
              </span>
            )}
          </button>
        ))}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Undo/Redo */}
        {activeTab === 'preview' && onUndo && onRedo && (
          <div className="flex items-center gap-0.5 mr-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 relative"
              onClick={onUndo}
              disabled={!canUndo}
              title={`Undo (${undoCount})`}
            >
              <Undo2 className="h-3.5 w-3.5" />
              {undoCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 text-[9px] bg-primary text-primary-foreground rounded-full h-3.5 min-w-[14px] flex items-center justify-center">
                  {undoCount}
                </span>
              )}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 relative"
              onClick={onRedo}
              disabled={!canRedo}
              title={`Redo (${redoCount})`}
            >
              <Redo2 className="h-3.5 w-3.5" />
              {redoCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 text-[9px] bg-primary text-primary-foreground rounded-full h-3.5 min-w-[14px] flex items-center justify-center">
                  {redoCount}
                </span>
              )}
            </Button>
          </div>
        )}

        {/* Preview theme toggle */}
        {activeTab === 'preview' && onPreviewThemeToggle && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={onPreviewThemeToggle}
            title={`Preview theme: ${previewTheme || 'light'}`}
          >
            {previewTheme === 'dark' ? (
              <Sun className="h-3.5 w-3.5" />
            ) : (
              <Moon className="h-3.5 w-3.5" />
            )}
          </Button>
        )}

        {/* Full-screen toggle — the side panel is cramped for real use; let
            the user see the arche at actual size. Rendered on every tab (a
            large Graph/Code view is just as useful). */}
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={() => setIsFullscreen((v) => !v)}
          aria-pressed={isFullscreen}
          title={isFullscreen ? 'Exit full screen (Esc)' : 'Full screen'}
        >
          {isFullscreen ? (
            <Minimize2 className="h-3.5 w-3.5" />
          ) : (
            <Maximize2 className="h-3.5 w-3.5" />
          )}
        </Button>

        {/* Settings gear — Deep Thinking / Verbose toggles + Build / Reset
            actions, consolidated to keep the toolbar uncluttered. The AI drives
            builds as needed, so manual Build is a rarely-used override; it and
            the destructive Reset live behind the gear. Rendered on every tab —
            Deep changes the next AI turn, not just the preview. */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 relative"
              title="Build settings & actions"
            >
              <Settings className="h-3.5 w-3.5" />
              {(isBuildingPreview || isResettingData) && (
                <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-primary animate-pulse" />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 p-2">
            {/* Header — an explicit close for users who don't realize the gear
                toggles it shut; clicking the gear again still closes it too. */}
            <div className="mb-1 flex items-center justify-between border-b px-2 pb-1.5">
              <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                Settings
              </span>
              <PopoverClose asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6" title="Close">
                  <X className="h-3.5 w-3.5" />
                </Button>
              </PopoverClose>
            </div>

            {/* Deep Thinking — all users; the only opt-in to Opus */}
            {onDeepThinkingChange && (
              <div className="flex items-start justify-between gap-3 px-2 py-1.5">
                <Label htmlFor="deep-thinking" className="cursor-pointer">
                  <div className="text-xs font-medium">Deep thinking</div>
                  <div className="text-[10px] font-normal leading-tight text-muted-foreground">
                    Uses Opus for higher quality. Costs more credits per turn.
                  </div>
                </Label>
                <Switch
                  id="deep-thinking"
                  checked={deepThinking}
                  onCheckedChange={onDeepThinkingChange}
                />
              </div>
            )}

            {/* Verbose (internal timeline) — admin only */}
            {isAdmin && onVerboseChange && (
              <div className="flex items-center justify-between gap-3 px-2 py-1.5">
                <Label htmlFor="verbose-mode" className="cursor-pointer text-xs font-medium">
                  Verbose
                </Label>
                <Switch id="verbose-mode" checked={verbose} onCheckedChange={onVerboseChange} />
              </div>
            )}

            {/* Screen size — preview device width (applies on the Preview tab) */}
            <div className="flex items-center justify-between gap-3 px-2 py-1.5">
              <span className="text-xs font-medium">Screen size</span>
              <div className="flex items-center gap-0.5">
                {[
                  { size: 'desktop' as DeviceSize, icon: Monitor },
                  { size: 'tablet' as DeviceSize, icon: Tablet },
                  { size: 'mobile' as DeviceSize, icon: Smartphone },
                ].map(({ size, icon: Icon }) => (
                  <Button
                    key={size}
                    variant={deviceSize === size ? 'default' : 'ghost'}
                    size="icon"
                    className="h-6 w-6"
                    onClick={() => setDeviceSize(size)}
                    title={size}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </Button>
                ))}
              </div>
            </div>

            {/* Settings above, actions below */}
            <div className="my-1.5 h-px bg-border" />

            {/* Build — manual rebuild override (the AI builds as needed) */}
            <button
              type="button"
              onClick={() => onBuildPreview()}
              disabled={isBuildingPreview || !files || fileCount === 0}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isBuildingPreview ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5" />
              )}
              Build
            </button>

            {/* Reset preview data — wipes the server-persisted preview tenant */}
            {onResetPreviewData && (
              <button
                type="button"
                onClick={async () => {
                  if (
                    !window.confirm(
                      "Reset preview data? This deletes everything your plugin has stored in the preview (files, collections, workspaces). The plugin itself is unaffected. If you have published, this does not touch your live arche's data — only the preview copy.",
                    )
                  ) {
                    return;
                  }
                  setIsResettingData(true);
                  try {
                    await onResetPreviewData();
                  } finally {
                    setIsResettingData(false);
                  }
                }}
                disabled={isResettingData}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10 disabled:cursor-not-allowed disabled:opacity-50"
                title="Delete all data the plugin has stored in the preview"
              >
                {isResettingData ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Eraser className="h-3.5 w-3.5" />
                )}
                Reset preview data
              </button>
            )}
          </PopoverContent>
        </Popover>
      </div>

      {/* Content — Preview is always mounted (hidden via CSS) to preserve iframe state */}
      <div className="flex-1 min-h-0 bg-background relative">
        <div
          className={
            activeTab === 'preview' ? 'h-full' : 'absolute inset-0 invisible pointer-events-none'
          }
        >
          <PreviewContent
            previewHtml={previewHtml}
            isBuildingPreview={isBuildingPreview}
            buildError={buildError}
            fileCount={fileCount}
            deviceWidth={DEVICE_WIDTHS[deviceSize]}
            previewTheme={previewTheme}
            iframeRef={iframeRef}
            hasPendingPreview={hasPendingPreview}
            onApplyPendingPreview={onApplyPendingPreview}
            onIframeLoad={handleIframeLoad}
          />
        </div>
        {activeTab === 'graph' ? (
          <GraphPane
            processGraph={processGraph}
            currentNodeId={currentNodeId}
            aiConfigs={aiConfigs}
            modelDefinitions={modelDefinitions}
            onPreviewNode={navigateToNode}
          />
        ) : activeTab === 'code' ? (
          <CodeViewerPane files={files} className="h-full" />
        ) : activeTab === 'log' ? (
          <LogContent entries={logEntries} onClear={onClearLogs ?? (() => {})} />
        ) : null}
      </div>
    </div>
  );
}

function PreviewContent({
  previewHtml,
  isBuildingPreview,
  buildError,
  fileCount,
  deviceWidth,
  previewTheme,
  iframeRef,
  onIframeLoad,
  hasPendingPreview,
  onApplyPendingPreview,
}: {
  previewHtml: string | null;
  isBuildingPreview: boolean;
  buildError: string | null;
  fileCount: number;
  deviceWidth: string;
  previewTheme?: 'light' | 'dark';
  iframeRef: React.RefObject<HTMLIFrameElement | null>;
  onIframeLoad: () => void;
  hasPendingPreview?: boolean;
  onApplyPendingPreview?: () => void;
}) {
  if (isBuildingPreview) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="text-sm">Building preview...</span>
      </div>
    );
  }

  if (buildError) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-3 p-6 text-center">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <div>
          <h4 className="text-sm font-semibold text-destructive mb-1">Build Error</h4>
          <pre className="text-xs text-muted-foreground whitespace-pre-wrap max-w-md">
            {buildError}
          </pre>
        </div>
      </div>
    );
  }

  if (!previewHtml) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-muted-foreground">
        <Eye className="h-8 w-8 opacity-30" />
        <span className="text-sm">
          {fileCount > 0 ? 'Click "Build" to preview your plugin' : 'No files to preview yet'}
        </span>
      </div>
    );
  }

  return (
    <div className="relative flex justify-center h-full bg-muted/30">
      {/* Deferred-swap pill (Phase 5): a fresher build exists but the user was
          interacting — never auto-yank the running app. No auto-apply timer:
          the swap happens only on the user's click (or a forced build). */}
      {hasPendingPreview && onApplyPendingPreview && (
        <button
          type="button"
          onClick={onApplyPendingPreview}
          className="absolute top-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 rounded-full border bg-background/95 px-3 py-1.5 text-xs font-medium shadow-sm hover:bg-muted transition-colors"
        >
          <RefreshCw className="h-3 w-3" />
          Preview updated — Refresh
        </button>
      )}
      <iframe
        ref={iframeRef}
        srcDoc={previewHtml}
        className="h-full border-0 transition-all"
        style={{
          width: deviceWidth,
          maxWidth: '100%',
          backgroundColor: previewTheme === 'light' ? '#ffffff' : '#0a0a0a',
        }}
        sandbox={PLUGIN_IFRAME_SANDBOX}
        allow={PLUGIN_IFRAME_ALLOW}
        title="Plugin Preview"
        onLoad={onIframeLoad}
      />
    </div>
  );
}
